<template>
  <Dialog
    :is-open="isOpen"
    title="Analytics"
    width="800px"
    @close="$emit('close')"
  >
    <!-- Filter Status Bar -->
    <div :class="$style.filterStatusBar">
      <span :class="$style.filterStatus">
        Analyzing {{ getFilterDescription() }}
      </span>
      <button
        :class="$style.editFiltersButton"
        @click="showFilterDialog = true"
      >
        <FilterIcon :size="16" />
      </button>
    </div>

    <!-- Time Management Insights -->
    <TimeManagementInsights :insights="insights" />

    <!-- Completion Metrics -->
    <CompletionMetrics :completion="completion" />

    <!-- Topic Analysis -->
    <TopicAnalysis :topic-analysis="topicAnalysis" />

    <!-- Daily Duration Chart -->
    <DailyDurationChart :daily-duration="dailyDuration" />

    <!-- Overrun Analysis -->
    <OverrunAnalysis :overrun-analysis="overrunAnalysis" />

    <!-- AI Summary Section -->
    <AISummary
      :has-api-key="hasApiKey"
      @show-settings="showGeminiSettings = true"
      @show-summary="showSummaryDialog = true"
    />
  </Dialog>

  <!-- Filter Dialog -->
  <FilterDialog
    v-model="showFilterDialog"
    :selected-topics="filters.selectedTopics"
    :selected-period="filters.selectedPeriod"
    :date-range="filters.dateRange"
    :selected-completion-status="filters.selectedCompletionStatus as CompletionStatus"
    :filter-by-sessions="filters.filterBySessions"
    @update:selected-topics="updateFilters('selectedTopics', $event)"
    @update:selected-period="updateFilters('selectedPeriod', $event)"
    @update:date-range="updateFilters('dateRange', $event)"
    @update:selected-completion-status="
      updateFilters('selectedCompletionStatus', $event)
    "
    @update:filter-by-sessions="updateFilters('filterBySessions', $event)"
  />

  <!-- AI Summary Dialog -->
  <SummaryDialog
    :is-open="showSummaryDialog"
    :tasks="tasks"
    :filters="filters"
    @close="showSummaryDialog = false"
  />

  <!-- Gemini Settings Dialog -->
  <GeminiSettingsDialog
    :is-open="showGeminiSettings"
    @close="showGeminiSettings = false"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import Dialog from "../Dialog.vue";
import FilterDialog from "../FilterDialog.vue";
import {
  analyticsService,
  type FilterSettings,
} from "../../services/analyticsService";
import { CompletionStatus } from "../../types/enums";
import type { Task } from "../../types/task";
import SummaryDialog from "../SummaryDialog.vue";
import GeminiSettingsDialog from "../GeminiSettingsDialog.vue";
import TimeManagementInsights from "./TimeManagementInsights.vue";
import CompletionMetrics from "./CompletionMetrics.vue";
import TopicAnalysis from "./TopicAnalysis.vue";
import DailyDurationChart from "./DailyDurationChart.vue";
import OverrunAnalysis from "./OverrunAnalysis.vue";
import AISummary from "./AISummary.vue";
import { FilterIcon } from "../icons";

const props = defineProps<{
  isOpen: boolean;
  tasks: Task[];
  filters: FilterSettings;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:filters", value: FilterSettings): void;
}>();

// Dialog states
const showSummaryDialog = ref(false);
const showGeminiSettings = ref(false);
const showFilterDialog = ref(false);
const hasApiKey = ref(false);

// Check if API key is configured
const checkApiKey = () => {
  const storedKey = localStorage.getItem("gemini-api-key");
  const envKey = import.meta.env.VITE_GEMINI_API_KEY;
  hasApiKey.value = !!(storedKey || envKey);
};

// Helper function to get human-readable filter description
const getFilterDescription = (): string => {
  const parts = [];

  if (props.filters.selectedPeriod !== "all") {
    const periodLabel = getPeriodLabel(props.filters.selectedPeriod);
    parts.push(periodLabel);
  }

  if (props.filters.selectedTopics.length > 0) {
    parts.push(`${props.filters.selectedTopics.length} topics`);
  }

  if (props.filters.selectedCompletionStatus !== "all") {
    const statusLabel = getCompletionStatusLabel(
      props.filters.selectedCompletionStatus
    );
    parts.push(statusLabel);
  }

  if (!props.filters.filterBySessions) {
    parts.push("task start time");
  }

  return parts.length > 0 ? parts.join(", ") : "all tasks";
};

// Helper functions for filter labels
const getPeriodLabel = (period: string): string => {
  switch (period) {
    case "today":
      return "today";
    case "week":
      return "this week";
    case "month":
      return "this month";
    case "year":
      return "this year";
    case "custom":
      return "custom range";
    default:
      return "all time";
  }
};

const getCompletionStatusLabel = (status: string): string => {
  switch (status) {
    case "completed":
      return "completed tasks";
    case "not-completed":
      return "incomplete tasks";
    default:
      return "all tasks";
  }
};

// Filter update function
const updateFilters = (key: string, value: any) => {
  const updatedFilters = { ...props.filters, [key]: value };
  emit("update:filters", updatedFilters);
};

// Calculate all analytics based on current filters and tasks
const insights = computed(() =>
  analyticsService.calculateTimeManagementInsights(props.tasks, props.filters)
);

const completion = computed(() =>
  analyticsService.calculateCompletionMetrics(props.tasks, props.filters)
);

const topicAnalysis = computed(() =>
  analyticsService.calculateTopicAnalysis(props.tasks, props.filters)
);

const overrunAnalysis = computed(() =>
  analyticsService.calculateOverrunAnalysis(props.tasks, props.filters)
);

const dailyDuration = computed(() =>
  analyticsService.calculateDailyDuration(props.tasks, props.filters)
);

// Listen for settings open event from SummaryDialog
onMounted(() => {
  checkApiKey();

  const handleOpenSettings = () => {
    showGeminiSettings.value = true;
  };

  window.addEventListener("open-gemini-settings", handleOpenSettings);
  window.addEventListener("storage", checkApiKey); // Listen for localStorage changes

  // Cleanup
  onUnmounted(() => {
    window.removeEventListener("open-gemini-settings", handleOpenSettings);
    window.removeEventListener("storage", checkApiKey);
  });
});
</script>

<style module>
.filterStatusBar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.filterStatus {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.editFiltersButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.editFiltersButton:hover {
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.05);
}
</style>
